import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { FaEdit, FaTrash, FaPlus } from 'react-icons/fa';
import { useParams, useNavigate } from 'react-router-dom';
import TransactionModal from '../transactions/TransactionModal';
import { Link } from 'react-router-dom';
import { useSearchParams } from 'react-router-dom';
import ReactPaginate from 'react-paginate';
import { useAuth } from '../../../context/AuthContext';
import withPermission from '../../../utils/withPermission';
import Swal from 'sweetalert2';

const WithDeletePermission = withPermission(({ children }) => children, ['transaction-delete']);
const WithEditPermission = withPermission(({ children }) => children, ['transaction-edit']);
const WithCreatePermission = withPermission(({ children }) => children, ['transaction-create']);

const TransactionList = ({ bankDetails, accountId, fetchBankDetails, setBankDetails, setTemporarilyHidden, ledgerEntries = [], ledgerOpeningBalance = null, countries = [] }) => {
    const { permissions, companySettings } = useAuth();
    const [transactions, setTransaction] = useState([]);
    const [showModal, setShowModal] = useState(false);
    const [selectedTransactionId, setSelectedTransactionId] = useState(null);
    const [selectedTransactionData, setSelectedTransactionData] = useState(null);
    const [errors, setErrors] = useState({});
    const didFetchRef = useRef(false);
    const [searchParams] = useSearchParams();
    const [currentPage, setCurrentPage] = useState(1);
    const [pageCount, setPageCount] = useState(1);
    const { roles } = useAuth();
    const [searchQuery, setSearchQuery] = useState('');
    const debounceTimeoutRef = useRef(null);
    const [categories, setCategories] = useState([]);
    const [banks, setBanks] = useState([]);
    const [clients, setClients] = useState([]);
    const [projects, setProjects] = useState([]);
    const [vendors, setVendors] = useState([]);
    const [isEditMode, setIsEditMode] = useState(false);
    const [users, setUsers] = useState([]);

    const getExchangeRate = (targetCurrencyCode) => {
        if (!targetCurrencyCode) return null;

        // 1. If target is USD, strictly check Company Settings/Settings first as requested
        if (targetCurrencyCode === 'USD') {
             if (companySettings?.usd_to_aed) return parseFloat(companySettings.usd_to_aed);
             const localRate = localStorage.getItem('exchange_rate');
             if (localRate) return parseFloat(localRate);
        }
        
        // 2. Fallback to Bank's custom exchange rates array
        if (bankDetails?.exchange_rates && Array.isArray(bankDetails.exchange_rates)) {
            const rateObj = bankDetails.exchange_rates.find(r => r.currency?.currency === targetCurrencyCode);
            if (rateObj) return parseFloat(rateObj.exchange_rate);
        }

        // 3. Fallback to Bank's deprecated exchange_rate field
        if (bankDetails?.exchange_rate && targetCurrencyCode === 'USD') {
             return parseFloat(bankDetails.exchange_rate);
        }

        return targetCurrencyCode === 'USD' ? 3.67 : null;
    };

    const displayConvertedAmount = (transaction) => {
        if (!transaction || !bankDetails) return transaction?.amount || '0.00';
        
        // Robust detection
        const bankCurrency = (bankDetails.base_currency?.currency || bankDetails.currency?.currency || bankDetails.currency || 'AED').toString().trim().toUpperCase();
        const transCurrency = (transaction.currency?.currency || transaction.currency || transaction.transaction_currency || 'AED').toString().trim().toUpperCase();
        
        let amount = parseFloat(transaction.amount) || 0;

        // 1. If currencies match, no conversion needed (Crucial fix: USD in USD bank stays USD)
        if (transCurrency === bankCurrency) {
            return amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
        }

        // 2. If currencies differ, check for bank-specific rate for the transaction's currency
        const rateObj = bankDetails.exchange_rates?.find(r => 
            (r.currency?.currency || '').toString().trim().toUpperCase() === transCurrency
        );

        if (rateObj) {
            const rate = parseFloat(rateObj.rate || rateObj.exchange_rate || 0);
            if (rate > 0) {
                // For bank specific entries: transAmount * rate = bankAmount (e.g. 100 AED * 0.27 = 27 USD)
                return (amount * rate).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
            }
        }

        // 3. Fallback to global settings
        const globalRate = parseFloat(companySettings?.usd_to_aed || localStorage.getItem('exchange_rate') || '3.67');
        
        // If trans is AED and bank is USD -> Divide (e.g. 367 AED / 3.67 = 100 USD)
        if (transCurrency === 'AED' && bankCurrency === 'USD') {
            return (amount / globalRate).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
        }

        // If trans is USD and bank is AED -> Multiply (e.g. 100 USD * 3.67 = 367 AED)
        if (transCurrency === 'USD' && bankCurrency === 'AED') {
            return (amount * globalRate).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
        }

        return amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    };


    useEffect(() => {
        if (!accountId) return;

        if (debounceTimeoutRef.current) {
            clearTimeout(debounceTimeoutRef.current);
        }

        const fetch = () => {
             fetchTransaction(1);
        };

        if (searchQuery) {
            debounceTimeoutRef.current = setTimeout(fetch, 1500);
        } else {
            fetch();
        }

        return () => {
            if (debounceTimeoutRef.current) {
                clearTimeout(debounceTimeoutRef.current);
            }
        };
    }, [searchQuery, accountId]);
    const handleSearchChange = (e) => {
        setSearchQuery(e.target.value);

    };

    const fetchTransaction = async (page = 1) => {
        try {
            const backendUrl = process.env.REACT_APP_BACKEND_URL;

            const userInfo = JSON.parse(localStorage.getItem('user-info'));
            const origin = localStorage.getItem('user-origin');

            const token = userInfo?.token;

            const response = await axios.get(`${backendUrl}/api/transactions`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Tenant-Origin': origin,

                },
                params: {
                    page,
                    account_id: accountId,
                    search: searchQuery,
                },
                skipLoader:true
            });

            const { data, current_page, last_page } = response.data.data;
            
            setTransaction(data);

            setCurrentPage(current_page - 1);
            setPageCount(last_page);
        } catch (error) {
            console.error('Error fetching transactions:', error);
        }
    };

    // Ledger fetching removed - now received from parent BankDetails.jsx

    const getTransactionRunningBalance = (transaction) => {
        if (!ledgerEntries.length) return null;

        const transDate = transaction.date;
        
        const type = (transaction.type || "").toLowerCase().trim();
        const advType = (transaction.advance_type || "").toLowerCase().trim();
        const entryTypeId = Number(transaction.entrytype_id || 0);

        const fromBankId = Number(transaction.from_bank_id || 0);
        const toBankId = Number(transaction.to_bank_id || 0);
        const targetAccId = Number(accountId);

        let isMoneyIn = ['income', 'receipt', 'capital', 'bank_guarantee_released'].includes(type) || 
                         (type === 'advance' && (advType === 'client' || advType === 'advance_receipt')) ||
                         (entryTypeId === 1);
        
        if (type === 'contra') {
            if (toBankId === targetAccId) isMoneyIn = true;
            else if (fromBankId === targetAccId) isMoneyIn = false;
        }

        // 1. Get the raw amount converted to bank's currency for matching with ledger
        const getRawConverted = () => {
            const bankCurrency = (bankDetails.base_currency?.currency || bankDetails.currency?.currency || bankDetails.currency || 'AED').toString().trim().toUpperCase();
            const transCurrency = (transaction.currency?.currency || transaction.currency || transaction.transaction_currency || 'AED').toString().trim().toUpperCase();
            let amt = parseFloat(transaction.amount) || 0;
            if (transCurrency === bankCurrency) return amt;
            const rateObj = bankDetails.exchange_rates?.find(r => (r.currency?.currency || '').toString().trim().toUpperCase() === transCurrency);
            if (rateObj) {
                const rate = parseFloat(rateObj.rate || rateObj.exchange_rate || 0);
                if (rate > 0) return amt * rate;
            }
            const globalRate = parseFloat(companySettings?.usd_to_aed || localStorage.getItem('exchange_rate') || '3.67');
            if (transCurrency === 'AED' && bankCurrency === 'USD') return amt / globalRate;
            if (transCurrency === 'USD' && bankCurrency === 'AED') return amt * globalRate;
            return amt;
        };

        const baseAmount = getRawConverted();
        
        // 2. Sum all charges to get the total impact on the bank account (Net Amount)
        const totalCharges = [
            transaction.bank_service_charge,
            transaction.other_bank_service_charge,
            transaction.bank_vat_amount,
            transaction.commission_fee,
            transaction.commission_vat,
            transaction.wps_charge,
            transaction.wps_vat,
            transaction.deduction_amount
        ].reduce((sum, val) => sum + (parseFloat(val) || 0), 0);

        const netAmount = baseAmount + (isMoneyIn ? -totalCharges : totalCharges);
        
        const transAmountNet = Math.abs(Math.round(netAmount * 100) / 100).toFixed(2);
        const transAmountGross = Math.abs(Math.round(baseAmount * 100) / 100).toFixed(2);
        const transDesc = (transaction.description || transaction.transaction || "").toLowerCase().trim();
        
        // Match against processed groups from ledgerEntries
        // We try to match Net first, then fallback to Gross if no matches found
        if (transAmountNet === '0.00' && transAmountGross === '0.00') {
            return null; // Zero amount transaction (like pure advance application) does not affect bank balance
        }

        let matches = ledgerEntries.filter(group => {
            const entryDate = group.date;
            const entryAmount = Math.abs(group.netLedgerAmount).toFixed(2);
            return entryDate === transDate && entryAmount === transAmountNet;
        });

        if (matches.length === 0 && transAmountNet !== transAmountGross) {
            matches = ledgerEntries.filter(group => {
                const entryDate = group.date;
                const entryAmount = Math.abs(group.netLedgerAmount).toFixed(2);
                return entryDate === transDate && entryAmount === transAmountGross;
            });
        }

        if (matches.length > 0) {
            // Use the global duplicate index from backend to match correctly across pagination
            const globalDupIndex = transaction.global_duplicate_index || 0;
            const match = matches[globalDupIndex] || matches[0];
            
            const rbAED = match.runningBalance;
            
            // Convert to bank currency
            const bankCurrency = (bankDetails.base_currency?.currency || bankDetails.currency?.currency || bankDetails.currency || 'AED').toString().trim().toUpperCase();
            let balance = rbAED;
            
            if (bankCurrency !== 'AED') {
                const aedRateObj = bankDetails.exchange_rates?.find(r => 
                    (r.currency?.currency || '').toString().trim().toUpperCase() === 'AED'
                );

                if (aedRateObj) {
                    const rate = parseFloat(aedRateObj.rate || aedRateObj.exchange_rate || 0);
                    if (rate > 0) balance = rbAED * rate;
                } else {
                    const globalRate = parseFloat(companySettings?.usd_to_aed || localStorage.getItem('exchange_rate') || '3.67');
                    if (globalRate > 0 && bankCurrency === 'USD') balance = rbAED / globalRate;
                }
            }

            const formatted = Math.abs(balance).toLocaleString('en-US', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
            });
            return balance >= 0 ? `Dr ${formatted}` : `Cr ${formatted}`;
        }
        return null;
    };
    const fetchCategories = async () => {
        try {
            const backendUrl = process.env.REACT_APP_BACKEND_URL;

            const token = JSON.parse(localStorage.getItem('user-info'))?.token;
            const origin = localStorage.getItem('user-origin');

            const response = await axios.get(`${backendUrl}/api/categories?type=expense`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Tenant-Origin': origin,

                },
                skipLoader: true,

            });
            setCategories(response.data.category);
        } catch (error) {
            console.error('Error fetching categories:', error);
        }
    };

    const fetchBanks = async (accountType = '') => {
        try {
            const backendUrl = process.env.REACT_APP_BACKEND_URL;
            const userInfo = JSON.parse(localStorage.getItem('user-info'));
            const origin = localStorage.getItem('user-origin');
            const token = userInfo?.token;

            // ✅ Remove check for accountType, allowing fetching all banks
            let url = `${backendUrl}/api/bank-accounts`;
            if (accountType) {
                url += `?type=${encodeURIComponent(accountType)}`; // Fetch specific type if provided
            }

            const response = await axios.get(url, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Tenant-Origin': origin,
                },
                skipLoader: true,
            });

            setBanks(response.data.account); // ✅ Update state with all banks

        } catch (error) {
            console.error('Error fetching banks:', error);
        }
    };
    const fetchClients = async () => {
        try {
            const backendUrl = process.env.REACT_APP_BACKEND_URL;

            const userInfo = JSON.parse(localStorage.getItem('user-info'));
            const origin = localStorage.getItem('user-origin');
            const token = userInfo?.token;

            const response = await axios.get(`${backendUrl}/api/contacts?type=client`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Tenant-Origin': origin,
                },
                skipLoader: true,

            });
            setClients(response.data.Data || []); // Ensure safe access
        } catch (error) {
            console.error('Error fetching clients:', error);
            Swal.fire({
                toast: true,
                position: 'top-end',
                icon: 'error',
                title: 'Failed to fetch clients. Please try again.',
                timer: 3000,
                timerProgressBar: false,
                showConfirmButton: false,
                didOpen: () => {
                    const container = Swal.getContainer();
                    container.style.top = '50px';
                },
            });
        }
    };
    const fetchVendors = async () => {
        try {
            const backendUrl = process.env.REACT_APP_BACKEND_URL;

            const userInfo = JSON.parse(localStorage.getItem('user-info'));
            const origin = localStorage.getItem('user-origin');
            const token = userInfo?.token;

            const response = await axios.get(`${backendUrl}/api/contacts?type=vendor`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Tenant-Origin': origin,
                },
                skipLoader: true,

            });
            setVendors(response.data.Data || []); // Ensure safe access
        } catch (error) {
            console.error('Error fetching vendors:', error);
            Swal.fire({
                toast: true,
                position: 'top-end',
                icon: 'error',
                title: 'Failed to fetch vendors. Please try again.',
                timer: 3000,
                timerProgressBar: false,
                showConfirmButton: false,
                didOpen: () => {
                    const container = Swal.getContainer();
                    container.style.top = '50px';
                },
            });
        }
    };
    
    const fetchUsers = async (page = 1) => {
            try {
                const backendUrl = process.env.REACT_APP_BACKEND_URL;
    
                const userInfo = JSON.parse(localStorage.getItem('user-info'));
                const origin = localStorage.getItem('user-origin');
    
                const token = userInfo?.token;
                const response = await axios.get(`${backendUrl}/api/users`, {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        'Tenant-Origin': origin,
                    },
                    skipLoader: true,
    
                });
                // const { data, current_page, last_page } = response.data.users;
    
                setUsers(response.data.users);
    
    
            } catch (error) {
                console.error('Error fetching users:', error);
            }
        };
    const fetchProjects = async (clientId) => {
        try {
            const backendUrl = process.env.REACT_APP_BACKEND_URL;
            const userInfo = JSON.parse(localStorage.getItem("user-info"));
            const origin = localStorage.getItem('user-origin');
            const token = userInfo?.token;

            if (!clientId) {
                setProjects([]); // ✅ Clear projects if no client selected
                return;
            }

            const response = await axios.get(`${backendUrl}/api/projects?client_id=${clientId}`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Tenant-Origin': origin,

                },
                skipLoader: true,

            });

            setProjects(response.data.Data || []); // ✅ Store only the selected client's projects
        } catch (error) {
            console.error("Error fetching projects:", error);
            setProjects([]); // ✅ Ensure dropdown is cleared on error
        }
    };

    const addTransaction = async (newTransaction) => {
        try {
            const backendUrl = process.env.REACT_APP_BACKEND_URL;

            const userInfo = JSON.parse(localStorage.getItem('user-info'));
            const origin = localStorage.getItem('user-origin');
            const token = userInfo?.token;

            const response = await axios.post(`${backendUrl}/api/transactions?account_id=${accountId}`, newTransaction, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Tenant-Origin': origin,
                },
            });

            const createdTransaction = response.data?.data;

            if (createdTransaction) {
                setTransaction((prevTransaction) => [...prevTransaction, createdTransaction]);
                setBankDetails((prevDetails) => {
                    if (!prevDetails) return prevDetails; // Handle case when details are not yet loaded

                    let updatedBalance = parseFloat(prevDetails.opening_balance) || 0;
                    const transactionAmount = parseFloat(createdTransaction.amount) || 0;

                    if (createdTransaction.type === 'income') {
                        updatedBalance += transactionAmount;
                    } else if (createdTransaction.type === 'expense') {
                        updatedBalance -= transactionAmount;
                    }

                    return {
                        ...prevDetails,
                        opening_balance: updatedBalance.toFixed(2),
                    };
                });
            } else {
                console.error('Creation failed, no data returned.');
            }


            handleCloseModal();
            // fetchBankDetails();
        } catch (error) {
            if (error.response?.data?.message) {
                return error.response.data.message; // Return validation errors
            } else {
                console.error('Error adding transaction:', error);
            }
        }
    };

    const editTransaction = async (transactionId, updatedData) => {
        try {
            const backendUrl = process.env.REACT_APP_BACKEND_URL;

            const userInfo = JSON.parse(localStorage.getItem('user-info'));
            const origin = localStorage.getItem('user-origin');
            const token = userInfo?.token;
            const existingTransaction = transactions.find((t) => t.id === transactionId);
            if (!existingTransaction) {
                console.error("Transaction not found locally.");
                return;
            }
            const response = await axios.put(`${backendUrl}/api/transactions/${transactionId}`, updatedData, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Tenant-Origin': origin,
                },
            });

            const updatedTransaction = response.data?.data;

            if (updatedTransaction) {
                setTransaction((prevTransaction) =>
                    prevTransaction.map((transaction) => (transaction.id === updatedTransaction.id ? updatedTransaction : transaction))
                );
                setBankDetails((prevDetails) => {
                    if (!prevDetails) return prevDetails;

                    let updatedBalance = parseFloat(prevDetails.opening_balance) || 0;
                    const oldAmount = parseFloat(existingTransaction.amount) || 0;
                    const newAmount = parseFloat(updatedTransaction.amount) || 0;

                    // **Reverse Old Transaction Effect**
                    if (existingTransaction.type === 'income') {
                        updatedBalance -= oldAmount;
                    } else if (existingTransaction.type === 'expense') {
                        updatedBalance += oldAmount;
                    }

                    // **Apply New Transaction Effect**
                    if (updatedTransaction.type === 'income') {
                        updatedBalance += newAmount;
                    } else if (updatedTransaction.type === 'expense') {
                        updatedBalance -= newAmount;
                    }

                    return {
                        ...prevDetails,
                        opening_balance: updatedBalance.toFixed(2),
                    };
                });
            } else {
                console.error('Update failed, no data returned.');
            }

            handleCloseModal();
        } catch (error) {
            if (error.response && error.response.data.message) {
                setErrors(error.response.data.message);
            } else {
                console.error('Error updating transaction:', error);
            }
        }
    };

    const deleteTransaction = async (id) => {
        Swal.fire({
            title: "Are you sure?",
            text: "You won't be able to revert this!",
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#d33",
            cancelButtonColor: "#3085d6",
            confirmButtonText: "Yes, delete it!"
        }).then(async (result) => {
            if (result.isConfirmed) {
                try {
                    const backendUrl = process.env.REACT_APP_BACKEND_URL;

                    const userInfo = JSON.parse(localStorage.getItem('user-info'));
                    const origin = localStorage.getItem('user-origin');
                    const token = userInfo?.token;

                    await axios.delete(` ${backendUrl}/api/transactions/${id}`, {
                        headers: {
                            'Authorization': `Bearer ${token}`,
                            'Tenant-Origin': origin,
                        },
                    });

                    setTransaction(transactions.filter(transaction => transaction.id !== id));
                    Swal.fire(
                        "Deleted!",
                        "The transaction has been deleted.",
                        "success"
                    );
                } catch (error) {
                    console.error('Error deleting transaction:', error);
                    Swal.fire(
                        "Error!",
                        "Failed to delete transaction. Please try again.",
                        "error"
                    );
                }
            }
        });
    };

    const handleShowModal = (transactionId = null, mode = "view") => {
        if (transactionId !== null) {
            const transactionToEdit = transactions.find(transaction => transaction.id === transactionId);
            setSelectedTransactionData(transactionToEdit || null);
            setSelectedTransactionId(transactionId);
        } else {
            setSelectedTransactionData(null);
            setSelectedTransactionId(null);
            setErrors({});
        }

        setIsEditMode(mode === "edit");
        setShowModal(true);
        if (setTemporarilyHidden) setTemporarilyHidden(true);
    };

    const handleCloseModal = () => {
        setShowModal(false);
        setIsEditMode(false);
        setSelectedTransactionId(null);
        setSelectedTransactionData(null);
        setErrors({});
        if (setTemporarilyHidden) setTemporarilyHidden(false);
    };

    const handlePageChange = ({ selected }) => {
        const newPage = selected + 1;
        setCurrentPage(newPage);
        fetchTransaction(newPage);
    };

    return (
        <React.Fragment>
            {/* Conditionally wrap content in card and card-body only if type is 'deal' */}
            <div>
                <div>
                    <h4 className="mb-sm-0 font-size-18"></h4>
                    <div className="d-flex justify-content-end mb-2">
                        {/* Search Box */}
                        <div className="search-box me-2">
                            <div className="position-relative">
                                <input
                                    type="text"
                                    className="form-control font-size-13"
                                    placeholder="Search..."
                                    value={searchQuery}
                                    onChange={handleSearchChange}
                                />
                                <i className="bx bx-search-alt search-icon" />
                            </div>
                        </div>

                        {/* Add Transaction Button */}
                        <WithCreatePermission>
                            <button
                                type="button"
                                className="btn rounded-circle border d-flex align-items-center justify-content-center round-plus"

                                onClick={() => {
                                    if (setTemporarilyHidden) setTemporarilyHidden(true);
                                    handleShowModal(null, "edit");
                                }}
                                data-bs-toggle="tooltip"
                                data-bs-placement="top"
                                id="tool"
                                title="Add Transaction"
                            >
                                <i className="mdi mdi-plus" style={{ fontSize: "16px" }}></i>
                            </button>
                        </WithCreatePermission>
                    </div><br />

                    <div className="table-responsive">
                        <table className="table table-hover align-middle table-nowrap mb-0 mt-2 font-size-11">
                            <thead style={{ background: 'linear-gradient(90deg, #f8f9fa 0%, #e9ecef 100%)' }}>
                                <tr className="font-size-10 text-uppercase fw-semibold">
                                    <th scope="col" className="ps-4" style={{ width: '50px' }}>#</th>
                                    <th scope="col" className="text-start">Transaction</th>
                                    <th scope="col">Type</th>
                                    <th scope="col">Date</th>
                                    <th scope="col">Amount ({(bankDetails?.base_currency?.currency || bankDetails?.currency?.currency || bankDetails?.currency || 'AED').toString().trim().toUpperCase()})</th>
                                    <th scope="col">Balance ({(bankDetails?.base_currency?.currency || bankDetails?.currency?.currency || bankDetails?.currency || 'AED').toString().trim().toUpperCase()})</th>
                                    <th scope="col" className="text-center">Actions</th>
                                </tr>
                            </thead>
                        <tbody>
                            {transactions.length > 0 ? (
                                transactions.map((transaction, index) => (
                                    <tr key={transaction.id}>
                                        <td className="ps-4 text-muted">{index + 1}</td>
                                        <td onClick={() => {
                                            if (setTemporarilyHidden) setTemporarilyHidden(true);
                                            handleShowModal(transaction.id, "view");
                                        }} className="cursor-pointer text-start">
                                            <div className="text-dark fw-medium font-size-11 text-wrap" style={{ maxWidth: '300px' }}>
                                                {transaction.description || transaction.transaction}
                                            </div>
                                        </td>
                                        <td onClick={() => {
                                            if (setTemporarilyHidden) setTemporarilyHidden(true);
                                            handleShowModal(transaction.id, "view");
                                        }} className="cursor-pointer">
                                            {(() => {
                                                const type = (transaction.type || "").toLowerCase().trim();
                                                const advType = (transaction.advance_type || "").toLowerCase().trim();
                                                const entryTypeId = Number(transaction.entrytype_id || 0);

                                                const isMoneyIn = ['income', 'receipt', 'capital', 'bank_guarantee_released'].includes(type) || 
                                                                 (type === 'advance' && (advType === 'client' || advType === 'advance_receipt')) ||
                                                                 (entryTypeId === 1);
                                                
                                                let label = type.charAt(0).toUpperCase() + type.slice(1) || 'Transaction';
                                                if (type === 'advance') {
                                                    if (advType === 'client' || advType === 'advance_receipt') label = 'Customer Advance';
                                                    else if (advType === 'vendor' || advType === 'advance_payment') label = 'Vendor Advance';
                                                    else if (advType === 'pettycash') label = 'Petty Cash Advance';
                                                    else label = 'Advance';
                                                }

                                                return (
                                                    <span className={`badge rounded-pill font-size-10 ${isMoneyIn ? 'bg-success-subtle text-success' : 'bg-danger-subtle text-danger'}`}>
                                                        {label}
                                                    </span>
                                                );
                                            })()}
                                        </td>
                                        <td onClick={() => {
                                            if (setTemporarilyHidden) setTemporarilyHidden(true);
                                            handleShowModal(transaction.id, "view");
                                        }} className="cursor-pointer">
                                            <div className="fw-medium text-dark">{transaction.date}</div>
                                        </td>
                                        <td>
                                            <div 
                                                className={`fw-bold font-size-11 ${(() => {
                                                    const type = (transaction.type || "").toLowerCase().trim();
                                                    const advType = (transaction.advance_type || "").toLowerCase().trim();
                                                    const entryTypeId = Number(transaction.entrytype_id || 0);

                                                    const isMoneyIn = ['income', 'receipt', 'capital', 'bank_guarantee_released'].includes(type) || 
                                                                     (type === 'advance' && (advType === 'client' || advType === 'advance_receipt')) ||
                                                                     (entryTypeId === 1);
                                                    return isMoneyIn ? 'text-success' : 'text-danger';
                                                })()}`}
                                                title={transaction.currency?.currency !== bankDetails?.currency?.currency ? `Original: ${transaction.amount} ${transaction.currency?.currency}` : ''}
                                            >
                                                {(() => {
                                                    const type = (transaction.type || "").toLowerCase().trim();
                                                    const advType = (transaction.advance_type || "").toLowerCase().trim();
                                                    const entryTypeId = Number(transaction.entrytype_id || 0);

                                                    const isMoneyIn = ['income', 'receipt', 'capital', 'bank_guarantee_released'].includes(type) || 
                                                                     (type === 'advance' && (advType === 'client' || advType === 'advance_receipt')) ||
                                                                     (entryTypeId === 1);
                                                    return isMoneyIn ? '+' : '-';
                                                })()} {displayConvertedAmount(transaction)}
                                            </div>
                                        </td>
                                        <td>
                                            <div className="fw-semibold text-dark font-size-11">
                                                {getTransactionRunningBalance(transaction) || '-'}
                                            </div>
                                        </td>
                                        <td className="text-center">
                                            <div className="dropdown">
                                                <button
                                                    className="btn btn-light btn-sm dropdown-toggle hide-caret"
                                                    data-bs-toggle="dropdown"
                                                    aria-expanded="false"
                                                >
                                                    <i className="mdi mdi-dots-horizontal"></i>
                                                </button>
                                                <ul className="dropdown-menu dropdown-menu-end shadow-sm border-0">
                                                    <li>
                                                        <button 
                                                            className="dropdown-item d-flex align-items-center py-2"
                                                            onClick={() => {
                                                                if (setTemporarilyHidden) setTemporarilyHidden(true);
                                                                handleShowModal(transaction.id, "view");
                                                            }}
                                                        >
                                                            <i className="mdi mdi-eye-outline me-2 text-muted"></i> View
                                                        </button>
                                                    </li>
                                                    <li>
                                                        <WithEditPermission>
                                                            <button
                                                                className="dropdown-item d-flex align-items-center py-2"
                                                                onClick={() => {
                                                                    if (setTemporarilyHidden) setTemporarilyHidden(true);
                                                                    handleShowModal(transaction.id, "edit");
                                                                }}
                                                            >
                                                                <i className="mdi mdi-penci-outline me-2 text-muted"></i> Edit
                                                            </button>
                                                        </WithEditPermission>
                                                    </li>
                                                    <li>
                                                        <hr className="dropdown-divider opacity-50" />
                                                    </li>
                                                    <li>
                                                        <WithDeletePermission>
                                                            <button
                                                                className="dropdown-item d-flex align-items-center py-2 text-danger"
                                                                onClick={() => deleteTransaction(transaction.id)}
                                                            >
                                                                <i className="mdi mdi-trash-can-outline me-2"></i> Delete
                                                            </button>
                                                        </WithDeletePermission>
                                                    </li>
                                                </ul>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan="8">No transactions available</td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>

                <div className="d-flex justify-content-end pagination-sm">
                        <ReactPaginate
                            previousLabel={'<'}
                            nextLabel={'>'}
                            breakLabel={'...'}
                            pageCount={pageCount}
                            marginPagesDisplayed={2}
                            pageRangeDisplayed={3}
                            onPageChange={handlePageChange}
                            containerClassName={'pagination pagination-rounded justify-content-end mb-2'}
                            pageClassName={'page-item'}
                            pageLinkClassName={'page-link'}
                            previousClassName={'page-item previous'}
                            previousLinkClassName={'page-link'}
                            nextClassName={'page-item next'}
                            nextLinkClassName={'page-link'}
                            breakClassName={'page-item'}
                            breakLinkClassName={'page-link'}
                            activeClassName={'active'}
                            forcePage={currentPage}
                        />

                    </div>

                </div>
            </div>


            {/* Render the TransactionModal */}
            <TransactionModal
                showModal={showModal}
                handleClose={handleCloseModal}
                transactionId={selectedTransactionId}
                transactionData={selectedTransactionData}
                addTransaction={addTransaction}
                editTransaction={editTransaction}
                errors={errors}
                setErrors={setErrors}
                clients={clients}
                fetchClients={fetchClients}
                projects={projects}
                fetchProjects={fetchProjects}
                categories={categories}
                fetchCategories={fetchCategories}
                banks={banks}
                fetchBanks={fetchBanks}
                vendors={vendors}
                fetchVendors={fetchVendors}
                setBanks={setBanks}
                accountId={accountId}
                isEditMode={isEditMode}
                setIsEditMode={setIsEditMode}
                users={users}
                fetchUsers={fetchUsers}
                countries={countries}
            />
        </React.Fragment>
    );


};
export default TransactionList;








